import React, { useEffect, useMemo, useState } from 'react'
import { Tournament } from '../types'
import { apiGet, apiPost, apiDelete } from '../utils/api'

interface Props {
  tournament: Tournament
  onClose: () => void
}

interface ExpenseItem {
  name: string
  amount: number
}

const toNumber = (v: any) => {
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

const TournamentBudgetModal: React.FC<Props> = ({ tournament, onClose }) => {
  // До уточнения модели считаем бюджет локально, без сохранения в БД

  // Доходы бюджета: сумма взносов по уровням (players * fee)
  let income = 0 // будет вычислен после инициализации byLevel ниже

  // Ставка аренды: приоритет courtRentalPrice, затем тариф клуба
  const defaultCourtRate = useMemo(() => {
    if ((tournament as any).courtRentalPrice) return toNumber((tournament as any).courtRentalPrice)
    try {
      const ratesStr = (tournament.club as any)?.rates
      if (ratesStr) {
        const rates = JSON.parse(ratesStr)
        if (rates && rates.courtRental) return toNumber(rates.courtRental)
      }
    } catch {}
    return 0
  }, [tournament])

  const [courtRate, setCourtRate] = useState<number>(defaultCourtRate)

  // Уровни турнира
  const levels: string[] = useMemo(() => {
    const list: any[] = Array.isArray((tournament as any).memberships) ? (tournament as any).memberships : []
    return Array.from(new Set(list.map(m => m.level).filter(Boolean)))
  }, [tournament])

  // Параметры по уровням
  type LevelParams = {
    players: number
    fee: number
    courtHours: number
    courtRate: number
    games: number
    gameHours: number
    ballsPerGame: number
    lostPercent: number
    firstGradePercent: number // второй = 100 - first
    priceFirstGrade: number
    priceSecondGrade: number
    waterPerGame: number
    otherExpense: number
    cupsExpense?: number
    prizesExpense?: number
  }

  const [byLevel, setByLevel] = useState<Record<string, LevelParams>>({})
  const byLevelRef = React.useRef(byLevel)
  useEffect(()=>{ byLevelRef.current = byLevel },[byLevel])
  useEffect(() => {
    setByLevel(prev => {
      const next = { ...prev }
      levels.forEach(l => {
        if (!next[l]) next[l] = {
          players: 0,
          fee: 0,
          courtHours: 0,
          courtRate: 0,
          games: 0,
          gameHours: 2.0,
          ballsPerGame: 2,
          lostPercent: 5,
          firstGradePercent: 100,
          priceFirstGrade: 15,
          priceSecondGrade: 10,
          waterPerGame: 2,
          otherExpense: 0,
          cupsExpense: 0,
          prizesExpense: 0
        }
      })
      return next
    })
    // Пересчитать доход после инициализации уровней
    try {
      income = Object.values(byLevel).reduce((s: number, p: any) => s + toNumber((p as any).players) * toNumber((p as any).fee), 0)
    } catch {}
  }, [levels])

  // Выбор ТМЦ для «Мячи» и «Вода» + получение сред. с/с из остатков
  const [inventoryItems, setInventoryItems] = useState<Array<{id:string;name:string;unit?:string}>>([])
  const [ballsItemId, setBallsItemId] = useState('')
  const [waterItemId, setWaterItemId] = useState('')
  const [avgCost, setAvgCost] = useState<Record<string, number>>({}) // id -> average cost

  useEffect(() => {
    ;(async () => {
      try {
        const j = await apiGet<any>('/inventory-items', { limit: 1000 })
        const list = Array.isArray(j.items) ? j.items : []
        setInventoryItems(list.map((it:any)=>({id:it.id,name:it.name,unit:it.unit})))
      } catch {}
    })()
  }, [])

  const fetchAvg = async (id: string) => {
    if (!id) return 0
    try {
      const to = (tournament as any)?.startDate ? new Date((tournament as any).startDate).toISOString() : ''
      const r = await fetch(`/api/inventory-items/${id}/balance?mode=live${to ? `&to=${encodeURIComponent(to)}` : ''}`)
      const b = await r.json()
      const qty = Number(b.balance || 0)
      const amt = Number(b.amount || 0)
      const avg = qty > 0 ? (amt / qty) : 0
      setAvgCost(prev => ({ ...prev, [id]: avg }))
      return avg
    } catch { return 0 }
  }

  useEffect(() => { if (ballsItemId) fetchAvg(ballsItemId) }, [ballsItemId])
  useEffect(() => { if (waterItemId) fetchAvg(waterItemId) }, [waterItemId])

  // Часы аренды (теперь задаются на уровне) = сумма courtHours по уровням, fallback: games*gameHours
  const courtHours = useMemo(() => {
    return Object.values(byLevel).reduce((h, p:any) => h + (toNumber(p.courtHours) || (toNumber(p.games) * toNumber(p.gameHours))), 0)
  }, [byLevel])
  const courtCost = useMemo(() => {
    // если на уровне есть courtRate, считаем сумму по уровням; иначе используем общий courtRate
    const perLevelSum = Object.values(byLevel).reduce((s, p:any) => s + (toNumber(p.courtHours)|| (toNumber(p.games)*toNumber(p.gameHours))) * (toNumber(p.courtRate) || 0), 0)
    if (perLevelSum > 0) return perLevelSum
    return toNumber(courtHours) * toNumber(courtRate)
  }, [byLevel, courtHours, courtRate])

  // Стоимость мячей и воды по уровням
  const ballsAvg = avgCost[ballsItemId] || 0
  const waterAvg = avgCost[waterItemId] || 0

  const perLevelCalc = useMemo(() => {
    const map: Record<string, any> = {}
    levels.forEach(l => {
      const p = byLevel[l]
      if (!p) return
      const games = toNumber(p.games)
      const ballsUsed = games * toNumber(p.ballsPerGame)
      const ballsCost = ballsUsed * ballsAvg
      const lost = ballsUsed * (toNumber(p.lostPercent) / 100)
      const sellable = Math.max(ballsUsed - lost, 0)
      const firstQty = sellable * (toNumber(p.firstGradePercent) / 100)
      const secondQty = sellable - firstQty
      const revenue = firstQty * toNumber(p.priceFirstGrade) + secondQty * toNumber(p.priceSecondGrade)
      const waterQty = games * toNumber(p.waterPerGame)
      const waterCost = waterQty * waterAvg
      const other = toNumber(p.otherExpense)
      const cups = toNumber((p as any).cupsExpense)
      const court = (toNumber(p.courtHours)|| (games*toNumber(p.gameHours))) * (toNumber(p.courtRate)|| toNumber(courtRate))
      const prizes = toNumber((p as any).prizesExpense)
      const expense = court + ballsCost + waterCost + other + cups + prizes
      map[l] = { games, ballsUsed, ballsCost, lost, firstQty, secondQty, revenue, waterQty, waterCost, other, cups, court, prizes, expense }
    })
    return map
  }, [levels, byLevel, ballsAvg, waterAvg])

  const levelsRevenue = Object.values(perLevelCalc).reduce((s:any,c:any)=> s + (c as any).revenue, 0)
  const levelsExpenses = Object.values(perLevelCalc).reduce((s:any,c:any)=> s + (c as any).expense, 0)

  // Прочее по турниру
  const [tournamentOther, setTournamentOther] = useState<number>(0)

  const [expenses, setExpenses] = useState<ExpenseItem[]>([])
  const [snapshots, setSnapshots] = useState<Array<{id:string; label?:string|null; createdAt:string; payload?: string}>>([])
  const [activeSnapshotId, setActiveSnapshotId] = useState<string>('')
  const [activeSnapshotPayload, setActiveSnapshotPayload] = useState<any>(null)

  // Загрузка/сохранение черновика бюджета в localStorage
  useEffect(() => {
    try {
      const key = `tournament-budget-${(tournament as any)?.id}`
      const raw = localStorage.getItem(key)
      if (raw) {
        const s = JSON.parse(raw)
        if (s.byLevel) setByLevel(s.byLevel)
        if (s.ballsItemId) setBallsItemId(s.ballsItemId)
        if (s.waterItemId) setWaterItemId(s.waterItemId)
        if (typeof s.tournamentOther === 'number') setTournamentOther(s.tournamentOther)
        if (typeof s.courtRate === 'number') setCourtRate(s.courtRate)
        if (Array.isArray(s.expenses)) setExpenses(s.expenses)
      }
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(tournament as any)?.id])

  // Загрузка списка сохраненных версий бюджета
  useEffect(() => {
    ;(async () => {
      try {
        const list = await apiGet<any[]>(`/tournaments/${(tournament as any)?.id}/budget-snapshots`)
        setSnapshots(Array.isArray(list) ? list : [])
      } catch {}
    })()
  }, [(tournament as any)?.id])

  const handleSave = async () => {
    try {
      const key = `tournament-budget-${(tournament as any)?.id}`
      const otherExp = expenses.reduce((s, e) => s + toNumber(e.amount), 0)
      // Регистрации: total и по уровням (взнос, плановая сумма)
      const memberships: any[] = Array.isArray((tournament as any)?.memberships) ? (tournament as any).memberships : []
      const regMap: Record<string, { count: number; fee: number }> = {}
      for (const m of memberships) {
        const lvl = m?.level || '—'
        if (!regMap[lvl]) regMap[lvl] = { count: 0, fee: 0 }
        regMap[lvl].count += 1
        const f = toNumber(m?.fee)
        if (f > 0) regMap[lvl].fee = f // фиксируем последний ненулевой взнос для уровня
      }
      const registrationsByLevel = Object.keys(regMap).map(lvl => ({
        level: lvl,
        count: regMap[lvl].count,
        fee: regMap[lvl].fee,
        planned: regMap[lvl].fee * regMap[lvl].count
      }))
      const registrationsTotal = memberships.length
      const registrationsPlannedTotal = registrationsByLevel.reduce((s, r:any) => s + toNumber(r.planned), 0)
      const snapshot = {
        income,
        levelsRevenue,
        courtCost,
        levelsExpenses,
        tournamentOther,
        otherExpenses: otherExp,
        totalExpenses: courtCost + levelsExpenses + toNumber(tournamentOther) + otherExp,
        net: income + levelsRevenue - (courtCost + levelsExpenses + toNumber(tournamentOther) + otherExp),
        ballsAvg,
        waterAvg,
        registrations: {
          total: registrationsTotal,
          totalPlanned: registrationsPlannedTotal,
          byLevel: registrationsByLevel
        }
      }
      const payload = { byLevel, ballsItemId, waterItemId, tournamentOther, courtRate, expenses, snapshot }
      localStorage.setItem(key, JSON.stringify(payload))

      // Сохранить снимок в БД
      try {
        await apiPost(`/tournaments/${(tournament as any)?.id}/budget-snapshots`, { payload, label: new Date().toISOString() })
        // обновим список версий
        const list = await apiGet<any[]>(`/tournaments/${(tournament as any)?.id}/budget-snapshots`)
        setSnapshots(Array.isArray(list) ? list : [])
      } catch {}
    } catch {}
  }

  // Открыть сохраненный снимок и подставить значения в форму
  const openSnapshot = async (s: { id: string; payload?: string }) => {
    try {
      let data: any = null
      if (s?.payload) {
        data = JSON.parse(s.payload)
      } else if (s?.id) {
        const j = await apiGet<any>(`/tournaments/budget-snapshots/${s.id}`)
        data = j?.payloadParsed || (j?.payload ? JSON.parse(j.payload) : null)
      }
      if (!data) return
      setActiveSnapshotId(s.id)
      setActiveSnapshotPayload(data)
      if (data.byLevel) setByLevel(data.byLevel)
      if (data.ballsItemId) setBallsItemId(data.ballsItemId)
      if (data.waterItemId) setWaterItemId(data.waterItemId)
      if (typeof data.tournamentOther === 'number') setTournamentOther(data.tournamentOther)
      if (typeof data.courtRate === 'number') setCourtRate(data.courtRate)
      if (Array.isArray(data.expenses)) setExpenses(data.expenses)
      try { window.alert('Снимок загружен'); } catch {}
    } catch {}
  }

  useEffect(() => { setCourtRate(defaultCourtRate) }, [defaultCourtRate])

  const totalExpenses = useMemo(() => {
    return courtCost + levelsExpenses + toNumber(tournamentOther) + expenses.reduce((s, e) => s + toNumber(e.amount), 0)
  }, [courtCost, levelsExpenses, tournamentOther, expenses])

  const net = useMemo(() => income + levelsRevenue - totalExpenses, [income, levelsRevenue, totalExpenses])

  const updateExpense = (idx: number, field: keyof ExpenseItem, value: string) => {
    setExpenses(prev => prev.map((e, i) => i !== idx ? e : { ...e, [field]: field === 'amount' ? toNumber(value) : value }))
  }

  const addExpense = () => setExpenses(prev => [...prev, { name: '', amount: 0 }])
  const removeExpense = (idx: number) => setExpenses(prev => prev.filter((_, i) => i !== idx))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-30" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-lg border w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Бюджет турнира: {tournament.name}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose}>✕</button>
        </div>

        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="text-sm text-green-700">Доходы (взносы)</div>
              <div className="text-2xl font-semibold text-green-900">{income.toFixed(2)} MDL</div>
              <div className="text-xs text-green-700 mt-1">Участники: {(tournament as any)?.memberships?.length || 0}</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-sm text-red-700">Расходы</div>
              <div className="text-2xl font-semibold text-red-900">{totalExpenses.toFixed(2)} MDL</div>
            </div>
            <div className={`rounded-lg p-3 border ${net >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'}`}> 
              <div className="text-sm text-gray-700">Результат</div>
              <div className={`text-2xl font-semibold ${net >= 0 ? 'text-blue-900' : 'text-yellow-900'}`}>{net.toFixed(2)} MDL</div>
            </div>
          </div>

          {/* Аренда кортов */}
          <div className="border rounded-lg p-3">
            <div className="font-medium text-gray-900 mb-2">Аренда кортов</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Часы (авто по уровням)</label>
                <input type="number" readOnly value={courtHours.toFixed(2)} className="w-full border rounded px-3 py-2 bg-gray-50" />
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Ставка, MDL/час</label>
                <input type="number" step="0.01" value={courtRate} onChange={e => setCourtRate(toNumber(e.target.value))} className="w-full border rounded px-3 py-2" />
                <div className="text-xs text-gray-500 mt-1">Авто: courtRentalPrice/тариф клуба</div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Итого, MDL</label>
                <input type="number" readOnly value={courtCost.toFixed(2)} className="w-full border rounded px-3 py-2 bg-gray-50" />
              </div>
            </div>
          </div>

          {/* Настройки ТМЦ для себестоимости */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3">
              <div className="font-medium text-gray-900 mb-2">ТМЦ: Мячи</div>
              <select className="w-full border rounded px-3 py-2 mb-2" value={ballsItemId} onChange={e=>setBallsItemId(e.target.value)}>
                <option value="">— Выберите —</option>
                {inventoryItems.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
              <div className="text-sm text-gray-600">Средняя с/с: { (avgCost[ballsItemId]||0).toFixed(2) } MDL/шт</div>
            </div>
            <div className="border rounded-lg p-3">
              <div className="font-medium text-gray-900 mb-2">ТМЦ: Вода</div>
              <select className="w-full border rounded px-3 py-2 mb-2" value={waterItemId} onChange={e=>setWaterItemId(e.target.value)}>
                <option value="">— Выберите —</option>
                {inventoryItems.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
              <div className="text-sm text-gray-600">Средняя с/с: { (avgCost[waterItemId]||0).toFixed(2) } MDL/шт</div>
            </div>
          </div>

          {/* Параметры по уровням */}
          <div className="border rounded-lg p-3">
            <div className="font-medium text-gray-900 mb-3">Параметры по уровням</div>
            <div className="space-y-4">
              {levels.map(l => {
                const p = byLevel[l]
                const c = perLevelCalc[l] || {}
                return (
                  <div key={l} className="border rounded-md p-3">
                    <div className="text-sm font-medium text-gray-900 mb-2">Уровень: {l}</div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Кол-во игр</label>
                        <input type="number" className="w-full border rounded px-3 py-2" value={p?.games||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], games: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                <label className="block text-xs text-gray-600 mb-1">Длительность игры, ч</label>
                        <input type="number" step="0.1" className="w-full border rounded px-3 py-2" value={p?.gameHours||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], gameHours: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Мячей на игру</label>
                        <input type="number" className="w-full border rounded px-3 py-2" value={p?.ballsPerGame||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], ballsPerGame: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Вода на игру, шт</label>
                        <input type="number" className="w-full border rounded px-3 py-2" value={p?.waterPerGame||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], waterPerGame: toNumber(e.target.value) } }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Потери мячей, %</label>
                        <input type="number" className="w-full border rounded px-3 py-2" value={p?.lostPercent||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], lostPercent: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">1-й сорт от продаваемых, %</label>
                        <input type="number" className="w-full border rounded px-3 py-2" value={p?.firstGradePercent||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], firstGradePercent: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Цена 1-й сорт, MDL</label>
                        <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={p?.priceFirstGrade||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], priceFirstGrade: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Цена 2-й сорт, MDL</label>
                        <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={p?.priceSecondGrade||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], priceSecondGrade: toNumber(e.target.value) } }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Прочее (уровень), MDL</label>
                        <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={p?.otherExpense||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], otherExpense: toNumber(e.target.value) } }))} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Призы/Кубки (уровень), MDL</label>
                        <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={p?.cupsExpense||0} onChange={e=>setByLevel(prev=>({ ...prev, [l]: { ...prev[l], cupsExpense: toNumber(e.target.value) } }))} />
                      </div>
                      <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-5 gap-2 text-xs text-gray-700">
                        <div>Мячей всего: <b>{(c.ballsUsed||0).toFixed(2)}</b></div>
                        <div>Потери: <b>{(c.lost||0).toFixed(2)}</b></div>
                        <div>1-й сорт шт: <b>{(c.firstQty||0).toFixed(2)}</b></div>
                        <div>2-й сорт шт: <b>{(c.secondQty||0).toFixed(2)}</b></div>
                        <div>Себест. мячей: <b>{(c.ballsCost||0).toFixed(2)}</b></div>
                        <div>Себест. воды: <b>{(c.waterCost||0).toFixed(2)}</b></div>
                        <div>Выручка мячей: <b>{(c.revenue||0).toFixed(2)}</b></div>
                        <div>Прочие: <b>{(c.other||0).toFixed(2)}</b></div>
                        <div>Призы/Кубки: <b>{(c.cups||0).toFixed(2)}</b></div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Прочее по турниру */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border rounded-lg p-3">
              <div className="font-medium text-gray-900 mb-2">Прочее (турнир)</div>
              <input type="number" step="0.01" className="w-full border rounded px-3 py-2" value={tournamentOther} onChange={e=>setTournamentOther(toNumber(e.target.value))} />
            </div>
          </div>

          <div className="border rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium text-gray-900">Прочие расходы</div>
              <button className="text-sm px-2 py-1 border rounded hover:bg-gray-50" onClick={addExpense}>Добавить статью</button>
            </div>
            <div className="space-y-2">
              {expenses.map((e, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <input className="md:col-span-8 border rounded px-3 py-2" placeholder="Статья" value={e.name} onChange={ev => updateExpense(idx, 'name', ev.target.value)} />
                  <input type="number" step="0.01" className="md:col-span-3 border rounded px-3 py-2" placeholder="Сумма" value={e.amount} onChange={ev => updateExpense(idx, 'amount', ev.target.value)} />
                  <button className="md:col-span-1 border rounded px-3 py-2 hover:bg-red-50" onClick={() => removeExpense(idx)}>✕</button>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 border-t pt-3">
            <div className="flex items-center gap-2 text-xs text-gray-600 flex-wrap">
              <span className="text-gray-700">Версии:</span>
              {snapshots.length === 0 && <span className="text-gray-400">нет</span>}
              {snapshots.slice(0,6).map(s => (
                <div key={s.id} className="flex items-center gap-1">
                  <button
                    className={`px-2 py-1 border rounded ${activeSnapshotId===s.id ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 hover:bg-gray-100'}`}
                    onClick={() => openSnapshot(s)}
                    title="Открыть снимок"
                  >
                    {new Date(s.createdAt).toLocaleString('ru-RU')}{s.label ? ` (${s.label})` : ''}
                  </button>
                  <button
                    className="px-1 py-1 text-red-600 hover:text-red-700"
                    title="Удалить версию"
                    onClick={async () => {
                      try {
                        const ok = window.confirm('Удалить эту версию?')
                        if (!ok) return
                        await apiDelete(`/tournaments/budget-snapshots/${s.id}`)
                        // обновим список
                        const list = await apiGet<any[]>(`/tournaments/${(tournament as any)?.id}/budget-snapshots`)
                        setSnapshots(Array.isArray(list) ? list : [])
                        if (activeSnapshotId === s.id) {
                          setActiveSnapshotId('')
                          setActiveSnapshotPayload(null)
                        }
                      } catch {}
                    }}
                  >🗑️</button>
                </div>
              ))}
              {snapshots.length > 6 && <span className="text-gray-400">+{snapshots.length-6}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button className="px-3 py-2 text-sm rounded-md border text-gray-700 bg-white hover:bg-gray-50" onClick={handleSave}>Сохранить</button>
              <button className="px-3 py-2 text-sm rounded-md border text-gray-700 bg-white hover:bg-gray-50" onClick={onClose}>Закрыть</button>
            </div>
          </div>

          {activeSnapshotPayload && (
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-gray-900">Содержимое версии</div>
                <button
                  className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                  onClick={() => {
                    try {
                      const text = JSON.stringify(activeSnapshotPayload, null, 2)
                      if (navigator.clipboard?.writeText) {
                        navigator.clipboard.writeText(text)
                      }
                    } catch {}
                  }}
                >Копировать JSON</button>
              </div>
              <pre className="text-xs bg-gray-50 border rounded p-2 overflow-auto max-h-64 whitespace-pre-wrap">
{JSON.stringify(activeSnapshotPayload, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TournamentBudgetModal


